import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { UserContextProvider } from './store/useStore';
import ProtectedRoute from './components/ProtectedRoute';
import Navigation from './components/Navigation';
import ProfileImageCTA from './components/ProfileImageCTA';
import Dashboard from './pages/Dashboard';
import Vocabulary from './pages/Vocabulary';
import Login from './pages/Login';
import SignUp from './pages/SignUp';
import Practice from './pages/Practice';
import { Videos } from './pages/Videos';
import Themes from './pages/Themes';
import Theme from './pages/Theme';
import ThemeDetail from './pages/ThemeDetail';
import Translate from './pages/Translate';
import Landing from './pages/Landing';
import NavbarLanding from './components/NavbarLanding';
import * as Sentry from "@sentry/react";
import Chat from './pages/Chat';
import Hotjar from '@hotjar/browser';
import { subscribeUser } from './pushNotification';
import { initializeApp } from 'firebase/app';
import { firebaseConfig } from './lib/firebase';
import { WordProvider } from './contexts/WordContext';
import Day from './pages/Day';
import Activities from './pages/Activities';
import Ranking from './pages/Ranking';
import AchievementsPage from './pages/AchievementsPage';
import AchievementNotification from './components/gamification/AchievementNotification';
import MemoryMatchPage from './pages/MemoryMatchPage';
import CrimeGame from './pages/CrimeGame';
import TrainingPage from './pages/TrainingPage';
import ProfilePage from './pages/ProfilePage';
import PublicProfilePage from './pages/PublicProfilePage';
import Expressions from './pages/Expressions';
import SocialPage from './pages/SocialPage';
import ChallengesPage from './pages/ChallengesPage';
import ChallengeDetailPage from './pages/ChallengeDetailPage';
import ProvocationDetailsPage from './pages/ProvocationDetailsPage';
import ScrollToTop from './components/ScrollToTop';
import NewsAI from './pages/NewsAI';
import NewsArticle from './pages/NewsArticle';
import NewsActivities from './pages/NewsActivities';
import StopGamePage from './pages/StopGamePage';
import FriendSessionPage from './pages/FriendSessionPage';

// Initialize firebase from lib to ensure single instance
import { db } from './lib/firebase';
import { checkAndResetScores, checkAndResetMemoryScores, checkAndResetNewsScores } from './services/leaderboardService';

const siteId = 5246004;
const hotjarVersion = 6;

try {
  Hotjar.init(siteId, hotjarVersion);
} catch (error) {
  console.log(error);
}

if (typeof window !== 'undefined') {
  const globalAny = window as any;
  const replay = globalAny.__SENTRY_REPLAY__ || Sentry.replayIntegration();
  const tracing = globalAny.__SENTRY_TRACING__ || Sentry.browserTracingIntegration();
  globalAny.__SENTRY_REPLAY__ = replay;
  globalAny.__SENTRY_TRACING__ = tracing;

  if (!globalAny.__SENTRY_INIT__) {
    Sentry.init({
      dsn: "https://b0034ad0b3ca03ae1912692219670770@o282739.ingest.us.sentry.io/4508447201624064",
      integrations: [tracing, replay],
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 0.1,
    });
    globalAny.__SENTRY_INIT__ = true;
  }
}

function App() {

  try {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/firebase-messaging-sw.js')
        .then((registration) => {
          console.log('Service Worker registered with scope:', registration.scope);
        });
    }
  } catch (error) {
    console.error('Error registering service worker:', error);
  }

  useEffect(() => {
    // Check and reset global leaderboards if a new week/month has started
    checkAndResetScores().catch(console.error);
    checkAndResetMemoryScores().catch(console.error);
    checkAndResetNewsScores().catch(console.error);
  }, []);

  return (
    <AuthProvider>
      <WordProvider>
      <UserContextProvider>
        <Router>
          <ScrollToTop />
          <ProfileImageCTA />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignUp />} />
            <Route
              path="/"
              element={
                <>
                    <main className="">
                      <Landing />
                    </main>
                </>
              }
            />
            
            {/* If someday we need an example about how to use a ProtectedRoute
            <Route
              path="/dashboard"
              element={
                <>
                  <Navigation />
                  <div className="min-h-screen bg-gray-50">
                    <main className="flex-1 pb-16 md:pb-0 md:ml-64">
                      <Dashboard />
                    </main>
                  </div>
                  </>
              }
            /> */}
            <Route
              path="/dashboard"
              element={
                <>
                  <NavbarLanding isLanding={false} />
                  <Navigation />
                  <div className="min-h-screen bg-gray-50">
                    <main className="flex-1 pb-16 md:pb-0 md:ml-64">
                      <Dashboard />
                    </main>
                  </div>
                  </>
              }
            />
            <Route
              path="/memory-game"
              element={
                <>
                  <NavbarLanding isLanding={false} />
                  <Navigation />
                  <div className="min-h-screen from-purple-50 to-white">
                    <main className="flex-1 pb-16 md:pb-0 md:ml-64">
                      <MemoryMatchPage />
                    </main>
                  </div>
                  </>
              }
            />
            <Route
              path="/vocabulary"
              element={
                <>
                  <NavbarLanding isLanding={false} />
                  <Navigation />
                  <div className="min-h-screen bg-gray-50">
                    <main className="flex-1 pb-16 md:pb-0 md:ml-64">
                      <Vocabulary />
                    </main>
                  </div>
                  </>
              }
            />
            <Route
              path="/expressions"
              element={
                <>
                  <NavbarLanding isLanding={false} />
                  <Navigation />
                  <div className="min-h-screen bg-gray-50">
                    <main className="flex-1 pb-16 md:pb-0 md:ml-64">
                      <Expressions />
                    </main>
                  </div>
                  </>
              }
            />
            <Route
              path="/translate"
              element={
                <>
                  <NavbarLanding isLanding={false} />
                  <Navigation />
                  <div className="min-h-screen bg-gray-50">
                    <main className="flex-1 pb-16 md:pb-0 md:ml-64">
                      <Translate />
                    </main>
                  </div>
                  </>
              }
            />
            <Route
              path="/theme"
              element={
                <>
                  <NavbarLanding isLanding={false} />
                  <Navigation />
                  <div className="min-h-screen bg-gray-50">
                    <main className="flex-1 pb-16 md:pb-0 md:ml-64">
                      <Theme />
                    </main>
                  </div>
                  </>
              }
            />
            <Route
              path="/theme/:themeId"
              element={
                <>
                  <NavbarLanding isLanding={false} />
                  <Navigation />
                  <div className="min-h-screen bg-gray-50">
                    <main className="flex-1 pb-16 md:pb-0 md:ml-64">
                      <ThemeDetail />
                    </main>
                  </div>
                  </>
              }
            />
            <Route
              path="/speaking"
              element={
                <>
                  <NavbarLanding isLanding={false} />
                  <Navigation />
                  <div className="min-h-screen bg-gray-50">
                    <main className="flex-1 pb-16 md:pb-0 md:ml-64">
                      <Practice />
                    </main>
                  </div>
                  </>
              }
            />
            <Route
              path="/videos"
              element={
                <>
                  <NavbarLanding isLanding={false} />
                  <Navigation />
                  <div className="min-h-screen bg-gray-50">
                    <main className="flex-1 pb-16 md:pb-0 md:ml-64">
                      <Videos />
                    </main>
                  </div>
                  </>
              }
            />
             <Route
              path="/themes"
              element={
                <>
                  <NavbarLanding isLanding={false} />
                  <Navigation />
                  <div className="min-h-screen bg-gray-50">
                    <main className="flex-1 pb-16 md:pb-0 md:ml-64">
                      <Themes />
                    </main>
                  </div>
                  </>
              }
            />
            <Route
              path="/chat"
              element={
                <>
                  <NavbarLanding isLanding={false} />
                  <Navigation />
                  <div className="min-h-screen from-purple-50 to-white">
                    <main className="flex-1 pb-16 md:pb-0 md:ml-64">
                      <Chat />
                    </main>
                  </div>
                  </>
              }
            />
            <Route
              path="/day"
              element={
                <>
                  <NavbarLanding isLanding={false} />
                  <Navigation />
                  <div className="min-h-screen from-purple-50 to-white md:ml-64">
                    <main className="flex-1 pb-16 md:pb-0">
                      <Day />
                    </main>
                  </div>
                  </>
              }
            />
            
                        <Route
              path="/news"
              element={<NewsAI />}
            />
            <Route
              path="/news/article/:id"
              element={<NewsArticle />}
            />
            <Route
              path="/news/activities"
              element={<NewsActivities />}
            />
            
            <Route
              path="/activities"
              element={
                <>
                  <NavbarLanding isLanding={false} />
                  <Navigation />
                  <div className="min-h-screen from-purple-50 to-white">
                    <main className="flex-1 pb-16 md:pb-0 md:ml-64">
                      <Activities />
                    </main>
                  </div>
                  </>
              }
            />
            <Route
              path="/ranking"
              element={
                <>
                  <NavbarLanding isLanding={false} />
                  <Navigation />
                  <div className="min-h-screen from-purple-50 to-white">
                    <main className="flex-1 pb-16 md:pb-0 md:ml-64">
                      <Ranking />
                    </main>
                  </div>
                  </>
              }
            />
            <Route
              path="/achievements"
              element={
                <>
                  <NavbarLanding isLanding={false} />
                  <Navigation />
                  <div className="min-h-screen from-purple-50 to-white">
                    <main className="flex-1 pb-16 md:pb-0 md:ml-64">
                      <AchievementsPage />
                    </main>
                  </div>
                  </>
              }
            />
            <Route
              path="/crime-game"
              element={
                <>
                  <NavbarLanding isLanding={false} />
                  <Navigation />
                  <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white">
                    <main className="flex-1 pb-16 md:pb-0 md:ml-64">
                      <CrimeGame />
                    </main>
                  </div>
                </>
              }
            />
            <Route
              path="/stop-game"
              element={
                <>
                  <NavbarLanding isLanding={false} />
                  <Navigation />
                  <div className="min-h-screen bg-gray-50">
                    <main className="flex-1 pb-16 md:pb-0 md:ml-64">
                      <StopGamePage />
                    </main>
                  </div>
                </>
              }
            />
            <Route
              path="/training"
              element={<TrainingPage />}
            />
            <Route
              path="/profile"
              element={
                <>
                  <NavbarLanding isLanding={false} />
                  <Navigation />
                  <div className="min-h-screen bg-gray-50">
                    <main className="flex-1 pb-16 md:pb-0 md:ml-64">
                      <ProfilePage />
                    </main>
                  </div>
                </>
              }
            />
            <Route
              path="/profile/:userId"
              element={
                <>
                  <NavbarLanding isLanding={false} />
                  <Navigation />
                  <div className="min-h-screen bg-gray-50">
                    <main className="flex-1 pb-16 md:pb-0 md:ml-64">
                      <PublicProfilePage />
                    </main>
                  </div>
                </>
              }
            />
            <Route
              path="/social"
              element={
                <>
                  <NavbarLanding isLanding={false} />
                  <Navigation />
                  <div className="min-h-screen bg-gray-50">
                    <main className="flex-1 pb-16 md:pb-0 md:ml-64">
                      <SocialPage />
                    </main>
                  </div>
                </>
              }
            />
            <Route
              path="/challenges"
              element={
                <>
                  <NavbarLanding isLanding={false} />
                  <Navigation />
                  <div className="min-h-screen bg-gray-50">
                    <main className="flex-1 pb-16 md:pb-0 md:ml-64">
                      <ChallengesPage />
                    </main>
                  </div>
                </>
              }
            />
            <Route
              path="/challenges/:challengeId"
              element={
                <>
                  <NavbarLanding isLanding={false} />
                  <Navigation />
                  <div className="min-h-screen bg-gray-50">
                    <main className="flex-1 pb-16 md:pb-0 md:ml-64">
                      <ChallengeDetailPage />
                    </main>
                  </div>
                </>
              }
            />
            <Route
              path="/provocation/:id"
              element={
                <>
                  <NavbarLanding isLanding={false} />
                  <Navigation />
                  <div className="min-h-screen bg-gray-50">
                    <main className="flex-1 pb-16 md:pb-0 md:ml-64">
                      <ProvocationDetailsPage />
                    </main>
                  </div>
                </>
              }
            />
            <Route
              path="/session/:sessionId"
              element={<FriendSessionPage />}
            />
          </Routes>
        </Router>
      </UserContextProvider>
      </WordProvider>
    </AuthProvider>
  );
}

export default App;